import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { useEffect, useMemo, useRef, useState } from 'react'
import { data, redirect, Link, useFetcher } from 'react-router'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { SearchBar } from '#app/components/search-bar'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { Input } from '#app/components/ui/input.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { formatDay } from '#app/utils/misc.tsx'
import { requireUserWithRole } from '#app/utils/permissions.server.ts'
import useScrollSync from '#app/utils/scroll-sync.ts'
import { useOptionalAdminUser } from '#app/utils/user.ts'
import { type Route } from './+types/signup.ts'

type TotalType = { [key: number]: number }
type PositionDitchType = {
	// position - for <tr>
	[key: number]: {
		// ditch - for <td>
		[key: number]: UserScheduleType
	}
}
type UserScheduleType = {
	userId: string
	username: string
	display: string | null
	restricted: boolean | null
	portId: string
	ditch: number
	position: number
	hours: number | bigint | null
	updatedBy: string | null
}

export const SearchResultsSchema = z.array(
	z.object({
		userId: z.string(),
		username: z.string(),
		display: z.string(),
		restricted: z.boolean().nullable(),
		portId: z.string(),
		ditch: z.preprocess(
			(x) => (x ? x : undefined),
			z.coerce.number().int().min(1).max(9),
		),
		position: z.preprocess(
			(x) => (x ? x : undefined),
			z.coerce.number().int().min(1).max(99),
		),
		hours: z.preprocess(
			(x) => (x ? x : 0),
			z.coerce.number().multipleOf(0.5).min(0).max(99),
		),
		updatedBy: z.string().nullable(),
	}),
)
export async function loader({ request, params }: Route.LoaderArgs) {
	const searchTerm = new URL(request.url).searchParams.get('search')
	invariantResponse(params.date, 'Date parameter Not found', { status: 404 })
	if (searchTerm === '') return redirect(`/schedule/${params.date}/signup`)

	const schedule = await prisma.schedule.findFirst({
		select: {
			id: true,
			state: true,
			date: true,
			deadline: true,
			costPerHour: true,
			source: true,
		},
		where: { date: params.date },
	})
	invariantResponse(schedule?.id, 'Schedule Not found', { status: 404 })

	const like = `%${searchTerm ?? ''}%`
	const rawUserSchedules = await prisma.$queryRaw`
		SELECT User.id AS userId, User.username, User.display, User.restricted, 
					 Port.id AS portId, Port.ditch, Port.position, 
					 mid.hours, mid.updatedBy
			FROM User
			INNER JOIN Port ON User.id = Port.userId
			LEFT JOIN (
				 SELECT Schedule.id, UserSchedule.userId, UserSchedule.portId, UserSchedule.hours, UserSchedule.updatedBy
					 FROM Schedule 
					INNER JOIN UserSchedule ON Schedule.id = UserSchedule.scheduleId
					WHERE Schedule.id = ${schedule?.id}
			) mid
				 ON User.id = mid.userId
				AND Port.id = mid.portId
			WHERE User.active
				AND (User.username LIKE ${like} OR User.member LIKE ${like})
			ORDER BY Port.ditch, Port.position
	`

	const result = SearchResultsSchema.safeParse(rawUserSchedules)
	if (!result.success) {
		return data(
			{
				status: 'error',
				error: result.error.message,
				schedule: {
					id: null,
					date: null,
					state: null,
					deadline: null,
					costPerHour: null,
					source: null,
				},
				userSchedules: null,
				totals: null,
				canOpen: false,
				rows: null,
				cols: null,
			} as const,
			{ status: 400 },
		)
	}

	const totals: TotalType = {}
	const userSchedules: PositionDitchType = {}
	for (let user of result.data) {
		const { position, ditch, hours } = user
		if (!position || !ditch) continue
		if (!userSchedules[position]) userSchedules[position] = { [ditch]: user }
		else userSchedules[position][ditch] = user

		if (totals[ditch]) totals[ditch] += hours
		else totals[ditch] = hours
	}
	return data({
		status: 'idle',
		schedule,
		userSchedules,
		totals,
		error: null,
	} as const)
}

const SignupSchema = z.object({
	userId: z.string(),
	portId: z.string(),
	hours: z.coerce.number().multipleOf(0.5).min(0).max(99).nullable(),
})
export async function action({ request, params }: Route.ActionArgs) {
	const updatedBy = await requireUserWithRole(request, 'admin')
	const schedule = await prisma.schedule.findFirst({
		select: { id: true },
		where: { date: params.date },
	})
	invariantResponse(schedule, 'Schedule Not found', { status: 404 })
	const { id: scheduleId } = schedule

	const formData = await request.formData()
	const intent = formData.get('intent')

	if (intent === 'update-user-schedule') {
		const submission = await parseWithZod(formData, {
			schema: SignupSchema,
			async: true,
		})
		invariantResponse(submission.status === 'success', 'Invalid submission', {
			status: 404,
		})

		const { userId, portId, hours } = submission.value
		await upsertSchedule(userId, portId, hours ?? 0)
		return null
	}

	async function upsertSchedule(userId: string, portId: string, hours: number) {
		await prisma.userSchedule.upsert({
			create: {
				userId,
				scheduleId,
				portId,
				hours,
				updatedBy,
			},
			update: { hours, updatedBy },
			where: {
				userId_scheduleId_portId: { userId, scheduleId, portId },
			},
		})
	}
}

export default function ScheduleSignupRoute({
	loaderData: { status, schedule, userSchedules, totals, error },
}: Route.ComponentProps) {
	const userIsAdmin = useOptionalAdminUser()
	const { id: scheduleId, date: scheduleDate } = schedule

	const nodeRefA = useRef(null)
	const nodeRefB = useRef(null)

	const nodeRefs = useMemo(() => [nodeRefA, nodeRefB], [nodeRefA, nodeRefB])

	const { registerPane, unregisterPane } = useScrollSync({
		onSync: undefined,
		proportional: true,
		vertical: false,
		horizontal: true,
		enabled: true,
	})

	useEffect(() => {
		nodeRefs.forEach((nodeRef) => {
			if (nodeRef?.current) registerPane(nodeRef.current)
		})
		return () =>
			nodeRefs.forEach((nodeRef) => {
				if (nodeRef?.current) unregisterPane(nodeRef.current)
			})
	}, [nodeRefs, registerPane, unregisterPane])

	if (status === 'error') console.error(error)

	const [display, setDisplay] = useState({
		all: false,
		admin: false,
		previous: false,
	})

	const toggleShowAll = () =>
		setDisplay((prev) => ({ ...prev, all: !prev.all, previous: !prev.all }))
	const toggleAdminButtons = () => {
		if (display.admin)
			setDisplay((prev) => ({ ...prev, admin: false, all: prev.previous }))
		else setDisplay((prev) => ({ ...prev, admin: true, all: true }))
	}

	if (!scheduleId || !userSchedules || !Object.keys(userSchedules).length)
		return null
	return (
		<div className="text-align-webkit-center bg-background flex w-full flex-col items-center justify-center gap-1">
			<div className="container my-8 flex flex-col items-center justify-center gap-6">
				<h1 className="text-h1 text-center">
					Irrigation Sign-up Schedule for {schedule.date}
				</h1>
				<h2 className="text-h2">
					Sign-up Deadline {formatDay(schedule.deadline)} at 7pm
				</h2>
				<h3 className="text-h3 capitalize">
					Source: {schedule.source} | Cost Per Hour: ${schedule.costPerHour}
				</h3>
			</div>
			<div className="flex w-[63.5%] flex-row flex-wrap gap-2 p-0.5">
				<div className="my-1 flex flex-row space-x-2">
					<Button variant="outline" onClick={toggleShowAll} className="pb-2">
						Display {display.all ? 'Scheduled' : 'All'}
					</Button>
				</div>
				<div className="my-1 grow">
					<SearchBar
						action={`/schedule/${scheduleDate}/signup`}
						status={status}
						autoFocus
						autoSubmit
					/>
				</div>
				<div className="my-1 flex flex-row space-x-2">
					{userIsAdmin ? (
						<>
							{display.admin ? (
								<Button>
									<Link
										reloadDocument
										to={`/resources/download/signup/${scheduleDate}`}
									>
										<Icon name="download">Download</Icon>
									</Link>
								</Button>
							) : null}
							<Button
								variant="outline"
								className="text-secondary-foreground w-48"
								onClick={toggleAdminButtons}
							>
								{`${display.admin ? '▲ Hide' : '▼ Show'} Admin Buttons`}
							</Button>
						</>
					) : null}
				</div>
			</div>
			<header className="bg-background text-foreground sticky top-0 m-auto flex w-full flex-col items-center gap-6">
				<div
					className="bg-background text-foreground m-auto block w-full overflow-x-auto"
					ref={nodeRefA}
				>
					<table>
						<thead>
							<tr>
								{Object.entries(totals).map(([ditch, hours]) => (
									<th className="sticky top-0 p-0.5" key={`ditch-${ditch}`}>
										{hours > 0 || display.all ? (
											<p className="bg-primary-foreground text-body-lg mb-1 flex w-44 flex-col rounded-lg px-5 py-3 text-center">
												Ditch {ditch}
												<p className="text-body-md mb-2 w-full text-center">
													{hours} hours
												</p>
											</p>
										) : null}
									</th>
								))}
							</tr>
						</thead>
					</table>
				</div>
			</header>

			<div className="m-auto w-full" style={{ height: 'fill-available' }}>
				{status === 'idle' ? (
					userSchedules ? (
						<div
							className="m-auto block w-full overflow-x-auto overflow-y-auto"
							ref={nodeRefB}
						>
							<table>
								<tbody>
									{Object.keys(userSchedules).map((position) => (
										<tr key={`${position}`}>
											{Object.keys(totals).map((ditch) => {
												const userSchedule =
													userSchedules?.[Number(position)]?.[Number(ditch)]
												if (!userSchedules) return null
												return (
													<td className="p-0.5" key={`${ditch}${position}`}>
														{userSchedule &&
														(userSchedule.hours || display.all) ? (
															<UserCard
																showAdmin={display.admin}
																userSchedule={userSchedule}
															/>
														) : null}
													</td>
												)
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<p>No members found</p>
					)
				) : status === 'error' ? (
					<ErrorList errors={['There was an error parsing the results']} />
				) : null}
			</div>
		</div>
	)
}

function UserCard({
	showAdmin,
	userSchedule: {
		userId,
		display,
		restricted,
		portId,
		position,
		hours,
		updatedBy,
	},
}: {
	showAdmin: boolean
	userSchedule: UserScheduleType
}) {
	const fetcher = useFetcher()
	const intent = 'update-user-schedule'
	const handleChange = (hours: number) =>
		fetcher.submit({ intent, userId, portId, hours }, { method: 'POST' })

	return (
		<div
			className={`flex w-44 flex-col items-center justify-end rounded-lg border px-5 py-3 ${showAdmin && 'pr-1.5'} ${formatColors({ restricted, userId, updatedBy })} `}
		>
			<div className={`flex w-full flex-row items-center justify-between`}>
				<span className="text-body-sm col-span-3 overflow-hidden text-nowrap text-ellipsis">
					{position}: {display}
				</span>
				{showAdmin ? (
					<Input
						id="hours"
						className="w-12 text-right"
						defaultValue={String(hours) ?? ''}
						onChange={(e) => handleChange(Number(e.currentTarget.value))}
					/>
				) : (
					<span className="text-body-sm overflow-hidden text-right text-ellipsis">
						{Number(hours)}
					</span>
				)}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ params }) => {
	return [
		{ title: `Irrigation Sign-Up | ${params.date}` },
		{
			name: 'description',
			content: `Clearwater Farms 1 Irrigation Sign-Up`,
		},
	]
}

function formatColors({
	restricted,
	userId,
	updatedBy,
}: {
	userId: string
	restricted: boolean | null
	updatedBy: string | null
}) {
	if (restricted) return 'border-destructive bg-destructive/30'
	if (updatedBy === userId) return 'border-primary bg-secondary'
	if (updatedBy === 'signup-link')
		return 'border-primary text-white bg-blue-700'
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
